import { createClient } from '@supabase/supabase-js';
import { registrarFalloDeRed, registrarRespuesta, sinRedDelSistema } from '@/core/red/conectividad';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Faltan VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY. Copiá .env.example a .env y completalos.',
  );
}

/**
 * Cuánto se espera una petición antes de darla por perdida.
 *
 * Hacían falta dos números, no uno, porque equivocarse cuesta cosas distintas
 * según para qué lado va la petición.
 *
 * **Leer** tiene a dónde caerse: está la copia guardada en el teléfono. Cortar
 * temprano muestra los datos del último sync y sigue; cortar tarde deja a
 * alguien mirando una pantalla detenida. Antes esto eran doce segundos y se
 * notaba: cada transición a sin señal tardaba eso en reaccionar.
 *
 * **Escribir** no tiene a dónde caerse. Abandonar un envío que iba a llegar es
 * peor que esperarlo: en el mejor caso se reintenta —el alta de trabajo lleva
 * su propio identificador, así que reintentar no duplica— y en el peor el
 * servidor lo procesó y la aplicación no se enteró. Con conexión mala eso pasa
 * seguido, así que la escritura tiene la correa larga.
 */
const ESPERA_LECTURA = 4_000;
const ESPERA_ESCRITURA = 15_000;

/** Escribir es todo lo que no es `GET`. Las RPC de Supabase viajan por `POST`. */
function esLectura(init?: RequestInit): boolean {
  return (init?.method ?? 'GET').toUpperCase() === 'GET';
}

/**
 * El `fetch` de la aplicación.
 *
 * Hace dos cosas además de pedir: le pone un límite de tiempo, y **avisa si el
 * servidor contestó**. Ese aviso es lo que alimenta el estado de conectividad
 * de toda la aplicación.
 *
 * Está acá y no en cada pantalla por una razón que se descubrió en el teléfono:
 * `navigator.onLine` devolvió `true` en MODO AVIÓN, así que la aplicación creía
 * tener red y mostraba `TypeError: Failed to fetch` en tres pantallas. Preguntar
 * «¿tenés antena?» no sirve; lo que sirve es «¿te contestaron?», y eso sólo se
 * sabe acá, donde pasan todas las peticiones.
 *
 * Que el servidor conteste un error NO es un fallo de red: un 401 o un 500
 * significan que hay servidor. Lo que se mide es si se puede hablar con él.
 */
async function fetchConAviso(entrada: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // Si el sistema operativo ya dijo que no hay red, no hay nada que intentar.
  // Esperar el vencimiento acá serían segundos de pantalla detenida para
  // averiguar algo que el teléfono ya sabía. El error que se tira es de la
  // familia que `esErrorDeRed` reconoce, así que el resto de la aplicación lo
  // trata igual que a una petición que salió y no volvió.
  if (sinRedDelSistema()) {
    registrarFalloDeRed();
    throw new TypeError('Failed to fetch');
  }

  const abortador = new AbortController();
  const reloj = setTimeout(
    () => abortador.abort(),
    esLectura(init) ? ESPERA_LECTURA : ESPERA_ESCRITURA,
  );

  // Se respeta la señal que venga de afuera: si quien llama cancela, se cancela.
  const deAfuera = init?.signal;
  if (deAfuera) {
    if (deAfuera.aborted) abortador.abort();
    else deAfuera.addEventListener('abort', () => abortador.abort(), { once: true });
  }

  try {
    const r = await fetch(entrada, { ...init, signal: abortador.signal });
    registrarRespuesta();
    return r;
  } catch (e) {
    // Cancelado por quien llamó: no dice nada sobre la red.
    if (deAfuera?.aborted) throw e;
    registrarFalloDeRed();
    throw e;
  } finally {
    clearTimeout(reloj);
  }
}

/**
 * Cliente único de Supabase.
 *
 * La sesión se persiste como TOKEN en almacenamiento local, nunca como cookie
 * de dominio: es requisito para poder empaquetar el mismo frontend con
 * Capacitor más adelante (ARCHITECTURE §5.4).
 */
export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: { fetch: fetchConAviso },
});
